import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { 
  Calendar as CalendarIcon, 
  TrendingUp, 
  CheckCircle, 
  AlertCircle, 
  Clock, 
  Filter, 
  X,
  Target,
  Activity,
  ArrowRight,
  RefreshCw,
  Play,
  Eye,
  BarChart3,
  Sparkles,
  Lightbulb,
  AlertTriangle,
  Search,
  Zap
} from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Input } from '../../components/ui/input';
import { DatePicker } from '../../components/DatePicker';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { MultiSelectFilter } from '../../components/MultiSelectFilter';
import { getPlatformIcon } from '../../utils/platformIcons';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DATE_FIELD_OPTIONS = [
  { value: 'scheduled_date', label: 'Scheduled Date' },
  { value: 'created_at', label: 'Created' },
  { value: 'updated_at', label: 'Last Updated' },
  { value: 'content_received_date', label: 'Content Received' },
  { value: 'posted_date', label: 'Posted Date' }
];

const STATUS_COLORS = {
  'Yet to Shoot': '#94a3b8',
  'In Shoot': '#8b5cf6',
  'Yet to Edit': '#64748b',
  'In Editing': '#f59e0b',
  'In Corrections': '#ec4899',
  'Not Approved': '#ef4444',
  'Yet to Deliver': '#6b7280',
  'Out Delivered': '#06b6d4',
  'Waiting for Approval': '#f97316',
  'On-Hold': '#eab308',
  'Scheduled': '#3b82f6',
  'Published': '#84cc16',
  // Legacy statuses for backward compatibility
  'Completed': '#84cc16',
  'Pending': '#6b7280'
};

const SocialMediaDashboard = () => {
  const { token } = useAuth();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [dateField, setDateField] = useState('scheduled_date');
  const [createdByFilter, setCreatedByFilter] = useState([]); // additive multi-select, does not replace date filters
  const [users, setUsers] = useState([]);
  
  // AI Insights state
  const [aiInsight, setAiInsight] = useState('');
  const [loadingInsight, setLoadingInsight] = useState(false);

  useEffect(() => {
    loadOverview();
  }, [fromDate, toDate, dateField, createdByFilter]);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      // Non-fatal — Created By filter just shows no options
    }
  };

  const loadOverview = async () => {
    try {
      const params = {};
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      if (dateField) params.date_field = dateField;
      if (createdByFilter.length > 0) params.created_by = createdByFilter.join(',');

      const response = await axios.get(`${API}/social-media/analytics/overview`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setOverview(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Access denied. You do not have permission to access this module.');
      } else {
        toast.error('Failed to load overview');
      }
    } finally {
      setLoading(false);
    }
  };

  // Generate AI Insights
  const generateAiInsights = async () => {
    setLoadingInsight(true);
    try {
      const response = await axios.get(`${API}/social-media/analytics/ai-insights`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAiInsight(response.data.insight);
      toast.success('AI insights generated successfully');
    } catch (error) {
      toast.error('Failed to generate AI insights');
    } finally {
      setLoadingInsight(false);
    }
  };

  const clearDateFilters = () => {
    setFromDate('');
    setToDate('');
    setDateField('scheduled_date');
    setCreatedByFilter([]);
    toast.success('Filters cleared');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  // Primary metrics
  const primaryMetrics = [
    { label: 'Total Posts', value: overview?.total_planned || 0, icon: CalendarIcon, color: 'from-slate-600 to-slate-700' },
    { label: 'Completion Rate', value: `${overview?.completion_rate || 0}%`, icon: Target, color: 'from-emerald-500 to-green-600' },
    { label: 'Published', value: overview?.completed || 0, icon: CheckCircle, color: 'from-lime-500 to-green-500' },
    { label: 'In Progress', value: overview?.in_progress || 0, icon: Activity, color: 'from-blue-500 to-cyan-500' },
  ];

  // Alert metrics
  const alertMetrics = [
    { label: 'Scheduled Today', value: overview?.scheduled_today || 0, icon: Play, color: 'from-cyan-500 to-blue-500', bgColor: 'bg-cyan-100' },
    { label: 'Upcoming (7 Days)', value: overview?.upcoming || 0, icon: Clock, color: 'from-amber-500 to-yellow-500', bgColor: 'bg-amber-100' },
    { label: 'Overdue', value: overview?.overdue || 0, icon: AlertCircle, color: 'from-red-500 to-rose-500', bgColor: 'bg-red-100', isAlert: true },
  ];

  // Status distribution for pie chart
  const statusData = Object.entries(overview?.status_distribution || {}).map(([name, value]) => ({
    name,
    value
  }));

  // Platform data for bar chart
  const platformData = Object.entries(overview?.platform_distribution || {}).map(([name, value]) => ({
    name,
    value
  }));

  return (
    <div className="space-y-6" data-testid="social-media-dashboard">
      <div className="page-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="page-title">Social Media Dashboard</h1>
            <p className="page-subtitle">Content scheduling and performance overview</p>
          </div>
          <Button onClick={loadOverview} variant="outline" className="flex items-center gap-2">
            <RefreshCw size={16} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Date Range Filter */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Filter size={18} className="text-slate-600" />
            <span className="text-sm font-semibold text-slate-700">Filter:</span>
          </div>
          
          <div className="flex items-center gap-2">
            <Label className="text-sm text-slate-600">Date Field:</Label>
            <Select value={dateField} onValueChange={setDateField}>
              <SelectTrigger className="w-[150px]" data-testid="dashboard-date-field-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DATE_FIELD_OPTIONS.map(option => (
                  <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex items-center gap-2">
            <Label className="text-sm text-slate-600">From:</Label>
            <DatePicker
              value={fromDate}
              onChange={setFromDate}
              className="w-36"
              testId="dashboard-from-date"
            />
          </div>
          
          <div className="flex items-center gap-2">
            <Label className="text-sm text-slate-600">To:</Label>
            <DatePicker
              value={toDate}
              onChange={setToDate}
              className="w-36"
              testId="dashboard-to-date"
            />
          </div>
          
          <div className="flex items-center gap-2">
            <Label className="text-sm text-slate-600">Created By:</Label>
            <MultiSelectFilter
              label="Created By"
              placeholder="All Users"
              triggerClassName="w-44"
              options={users.map(u => ({ value: u.id, label: u.full_name, searchText: u.email }))}
              selectedValues={createdByFilter}
              onChange={setCreatedByFilter}
              testId="dashboard-created-by-filter"
            />
          </div>
          
          {(fromDate || toDate || dateField !== 'scheduled_date' || createdByFilter.length > 0) && (
            <Button variant="outline" size="sm" onClick={clearDateFilters} className="text-slate-600">
              <X size={16} className="mr-1" /> Clear
            </Button>
          )}
        </div>
      </div>

      {/* Platform Status Breakdown */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
        <h2 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
          <BarChart3 className="text-cyan-500" size={20} />
          Platform Performance
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {Object.entries(overview?.platform_status || {}).map(([platform, stats]) => {
            const total = (stats.completed || 0) + (stats.in_progress || 0) + (stats.pending || 0);
            const completionRate = total > 0 ? Math.round((stats.completed / total) * 100) : 0;
            
            return (
              <div key={platform} className="bg-slate-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  {getPlatformIcon(platform, 24)}
                  <span className="font-semibold text-slate-800">{platform}</span>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Total</span>
                    <span className="font-semibold">{total}</span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2">
                    <div 
                      className="bg-green-500 h-2 rounded-full transition-all" 
                      style={{ width: `${completionRate}%` }}
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center text-xs">
                    <div>
                      <p className="font-bold text-green-600">{stats.completed || 0}</p>
                      <p className="text-slate-500">Done</p>
                    </div>
                    <div>
                      <p className="font-bold text-blue-600">{stats.in_progress || 0}</p>
                      <p className="text-slate-500">Active</p>
                    </div>
                    <div>
                      <p className="font-bold text-slate-500">{stats.pending || 0}</p>
                      <p className="text-slate-500">Pending</p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          {Object.keys(overview?.platform_status || {}).length === 0 && (
            <p className="col-span-full text-center text-slate-500 py-8">No platform data available</p>
          )}
        </div>
      </div>

      {/* Upcoming & Overdue Posts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Upcoming Posts */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <Clock className="text-amber-500" size={20} />
              Upcoming Posts
            </h2>
            <Link to="/social-media/posts" className="text-sm text-cyan-600 hover:text-cyan-700 flex items-center gap-1">
              View all <ArrowRight size={14} />
            </Link>
          </div>
          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            {(overview?.upcoming_posts || []).length > 0 ? (
              overview.upcoming_posts.map((post) => (
                <div 
                  key={post.id} 
                  className={`p-3 rounded-lg border ${post.is_today ? 'bg-cyan-50 border-cyan-200' : 'bg-amber-50 border-amber-100'}`}
                >
                  <p className="font-medium text-slate-800 text-sm truncate">{post.content_name}</p>
                  <div className="flex items-center justify-between mt-1">
                    <div className="flex items-center gap-1">
                      {post.platforms?.slice(0, 2).map(p => (
                        <span key={p}>{getPlatformIcon(p, 14)}</span>
                      ))}
                      {post.platforms?.length > 2 && (
                        <span className="text-xs text-slate-500">+{post.platforms.length - 2}</span>
                      )}
                    </div>
                    <span className={`text-xs font-medium ${post.is_today ? 'text-cyan-600' : 'text-amber-600'}`}>
                      {post.is_today ? 'Today' : post.scheduled_date ? format(new Date(post.scheduled_date), 'MMM dd') : 'N/A'}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <CheckCircle className="mx-auto text-green-500 mb-2" size={32} />
                <p className="text-slate-500 text-sm">No upcoming posts scheduled</p>
              </div>
            )}
          </div>
        </div>

        {/* Overdue Posts */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <AlertCircle className="text-red-500" size={20} />
              Overdue Posts
            </h2>
            <Link to="/social-media/posts" className="text-sm text-cyan-600 hover:text-cyan-700 flex items-center gap-1">
              View all <ArrowRight size={14} />
            </Link>
          </div>
          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            {(overview?.overdue_posts || []).length > 0 ? (
              overview.overdue_posts.map((post) => (
                <div key={post.id} className="p-3 bg-red-50 rounded-lg border border-red-100">
                  <p className="font-medium text-slate-800 text-sm truncate">{post.content_name}</p>
                  <div className="flex items-center justify-between mt-1">
                    <div className="flex items-center gap-1">
                      {post.platforms?.slice(0, 2).map(p => (
                        <span key={p}>{getPlatformIcon(p, 14)}</span>
                      ))}
                    </div>
                    <span className="text-xs text-red-600 font-medium">
                      Due: {post.scheduled_date ? format(new Date(post.scheduled_date), 'MMM dd') : 'N/A'}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <CheckCircle className="mx-auto text-green-500 mb-2" size={32} />
                <p className="text-slate-500 text-sm">No overdue posts!</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Distribution Cards Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Account Distribution */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <h2 className="text-lg font-bold text-slate-800 mb-4">By Account</h2>
          <div className="space-y-2">
            {Object.entries(overview?.account_distribution || {}).map(([account, count]) => (
              <div key={account} className="flex justify-between items-center p-2 bg-slate-50 rounded-lg">
                <span className="text-sm text-slate-700">{account}</span>
                <span className="font-bold text-slate-900">{count}</span>
              </div>
            ))}
            {Object.keys(overview?.account_distribution || {}).length === 0 && (
              <p className="text-center text-slate-500 py-4 text-sm">No accounts</p>
            )}
          </div>
        </div>

        {/* College Distribution */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <h2 className="text-lg font-bold text-slate-800 mb-4">By College</h2>
          <div className="space-y-2">
            {Object.entries(overview?.college_distribution || {}).map(([college, count]) => (
              <div key={college} className="flex justify-between items-center p-2 bg-slate-50 rounded-lg">
                <span className="text-sm text-slate-700">{college}</span>
                <span className="font-bold text-slate-900">{count}</span>
              </div>
            ))}
            {Object.keys(overview?.college_distribution || {}).length === 0 && (
              <p className="text-center text-slate-500 py-4 text-sm">No colleges</p>
            )}
          </div>
        </div>

        {/* Content Type Distribution */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <h2 className="text-lg font-bold text-slate-800 mb-4">By Content Type</h2>
          <div className="space-y-2">
            {Object.entries(overview?.content_type_distribution || {}).map(([type, count]) => (
              <div key={type} className="flex justify-between items-center p-2 bg-slate-50 rounded-lg">
                <span className="text-sm text-slate-700">{(!type || type === 'null') ? 'Uncategorized' : type}</span>
                <span className="font-bold text-slate-900">{count}</span>
              </div>
            ))}
            {Object.keys(overview?.content_type_distribution || {}).length === 0 && (
              <p className="text-center text-slate-500 py-4 text-sm">No content types</p>
            )}
          </div>
        </div>
      </div>

      {/* Status Overview Grid */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
        <h2 className="text-lg font-bold text-slate-800 mb-4">Detailed Status Breakdown</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {[
            { status: 'Yet to Shoot', color: 'bg-slate-100 text-slate-700 border-slate-200' },
            { status: 'In Shoot', color: 'bg-purple-100 text-purple-700 border-purple-200' },
            { status: 'Yet to Edit', color: 'bg-slate-100 text-slate-600 border-slate-200' },
            { status: 'In Editing', color: 'bg-amber-100 text-amber-700 border-amber-200' },
            { status: 'In Corrections', color: 'bg-pink-100 text-pink-700 border-pink-200' },
            { status: 'Not Approved', color: 'bg-red-100 text-red-700 border-red-200' },
            { status: 'Yet to Deliver', color: 'bg-gray-100 text-gray-600 border-gray-200' },
            { status: 'Out Delivered', color: 'bg-cyan-100 text-cyan-700 border-cyan-200' },
            { status: 'Waiting for Approval', color: 'bg-orange-100 text-orange-700 border-orange-200' },
            { status: 'On-Hold', color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
            { status: 'Scheduled', color: 'bg-blue-100 text-blue-700 border-blue-200' },
            { status: 'Published', color: 'bg-lime-100 text-lime-700 border-lime-200' },
          ].map(({ status, color }) => {
            const count = overview?.status_distribution?.[status] || 0;
            return (
              <div key={status} className={`rounded-lg p-3 text-center border ${color}`}>
                <p className="text-2xl font-bold">{count}</p>
                <p className="text-xs mt-1 leading-tight">{status}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Enhanced AI-Powered Insights Section */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden" data-testid="ai-insights-section">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-50 via-violet-50 to-fuchsia-50 border-b border-slate-100 p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-gradient-to-br from-purple-500 to-violet-600 text-white">
                <Sparkles size={22} />
              </div>
              <div>
                <h2 className="text-lg font-bold text-slate-800">Enhanced AI-Powered Insights</h2>
                <p className="text-sm text-slate-500">Smart recommendations based on your content activity</p>
              </div>
            </div>
            <Button
              onClick={generateAiInsights}
              disabled={loadingInsight}
              data-testid="generate-social-insights-btn"
              className="bg-gradient-to-r from-purple-500 to-violet-600 hover:from-purple-600 hover:to-violet-700"
            >
              <RefreshCw size={16} className={`mr-2 ${loadingInsight ? 'animate-spin' : ''}`} />
              {loadingInsight ? 'Analyzing...' : 'Generate Insights'}
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="p-5">
          {aiInsight && (
            <div className="space-y-4" data-testid="ai-insight-content">
              {/* Parse and render insight sections */}
              {aiInsight.split('##').filter(Boolean).map((section, idx) => {
                const lines = section.trim().split('\n');
                const title = lines[0]?.trim();
                const content = lines.slice(1).join('\n').trim();
                
                // Determine section styling based on title content
                let bgColor = 'bg-slate-50';
                let borderColor = 'border-slate-200';
                let IconComponent = Search;
                let iconBgColor = 'bg-slate-100';
                let iconTextColor = 'text-slate-600';
                
                if (title?.includes('Content Performance') || title?.includes('📈')) {
                  bgColor = 'bg-gradient-to-r from-blue-50 to-cyan-50';
                  borderColor = 'border-blue-200';
                  IconComponent = TrendingUp;
                  iconBgColor = 'bg-blue-100';
                  iconTextColor = 'text-blue-600';
                } else if (title?.includes('Posting Consistency') || title?.includes('⏰')) {
                  bgColor = 'bg-gradient-to-r from-amber-50 to-yellow-50';
                  borderColor = 'border-amber-200';
                  IconComponent = Clock;
                  iconBgColor = 'bg-amber-100';
                  iconTextColor = 'text-amber-600';
                } else if (title?.includes('Account') || title?.includes('College') || title?.includes('🏢')) {
                  bgColor = 'bg-gradient-to-r from-emerald-50 to-teal-50';
                  borderColor = 'border-emerald-200';
                  IconComponent = Target;
                  iconBgColor = 'bg-emerald-100';
                  iconTextColor = 'text-emerald-600';
                } else if (title?.includes('Platform') || title?.includes('📱')) {
                  bgColor = 'bg-gradient-to-r from-purple-50 to-pink-50';
                  borderColor = 'border-purple-200';
                  IconComponent = Activity;
                  iconBgColor = 'bg-purple-100';
                  iconTextColor = 'text-purple-600';
                } else if (title?.includes('Approval') || title?.includes('Workflow') || title?.includes('✅')) {
                  bgColor = 'bg-gradient-to-r from-orange-50 to-rose-50';
                  borderColor = 'border-orange-200';
                  IconComponent = AlertTriangle;
                  iconBgColor = 'bg-orange-100';
                  iconTextColor = 'text-orange-600';
                } else if (title?.includes('Recommendations') || title?.includes('💡')) {
                  bgColor = 'bg-gradient-to-r from-lime-50 to-green-50';
                  borderColor = 'border-lime-200';
                  IconComponent = Lightbulb;
                  iconBgColor = 'bg-lime-100';
                  iconTextColor = 'text-lime-600';
                }
                
                return title && content && (
                  <div 
                    key={idx} 
                    className={`${bgColor} border ${borderColor} rounded-xl p-4 transition-all hover:shadow-sm`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${iconBgColor} ${iconTextColor} shrink-0`}>
                        <IconComponent size={18} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-slate-800 mb-2">{title}</h3>
                        <div className="text-slate-700 text-sm leading-relaxed whitespace-pre-wrap">
                          {content}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          
          {!aiInsight && !loadingInsight && (
            <div className="text-center py-10">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-r from-purple-100 to-violet-100 flex items-center justify-center">
                <Sparkles className="text-purple-500" size={36} />
              </div>
              <p className="text-slate-700 font-semibold mb-2">Get AI-Powered Content Insights</p>
              <p className="text-slate-500 text-sm max-w-lg mx-auto mb-4">
                Click &quot;Generate Insights&quot; to receive personalized recommendations about your content performance, 
                posting consistency, platform usage, and workflow optimization.
              </p>
              <div className="flex flex-wrap justify-center gap-2 text-xs">
                <span className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-full flex items-center gap-1">
                  <TrendingUp size={12} /> Performance Trends
                </span>
                <span className="px-3 py-1.5 bg-amber-100 text-amber-700 rounded-full flex items-center gap-1">
                  <Clock size={12} /> Posting Gaps
                </span>
                <span className="px-3 py-1.5 bg-purple-100 text-purple-700 rounded-full flex items-center gap-1">
                  <Activity size={12} /> Platform Analysis
                </span>
                <span className="px-3 py-1.5 bg-lime-100 text-lime-700 rounded-full flex items-center gap-1">
                  <Lightbulb size={12} /> Smart Suggestions
                </span>
              </div>
            </div>
          )}
          
          {loadingInsight && (
            <div className="text-center py-10">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-r from-purple-100 to-violet-100 flex items-center justify-center animate-pulse">
                <Sparkles className="text-purple-500 animate-spin" size={36} />
              </div>
              <p className="text-slate-700 font-semibold mb-2">Analyzing Your Content Data...</p>
              <p className="text-slate-500 text-sm">
                AI is reviewing your content types, platforms, posting patterns, and workflow
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Primary Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {primaryMetrics.map((metric) => {
          const Icon = metric.icon;
          return (
            <div key={metric.label} className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{metric.label}</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">{metric.value}</p>
                </div>
                <div className={`p-2.5 rounded-lg bg-gradient-to-br ${metric.color} text-white`}>
                  <Icon size={20} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Alert Metrics Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {alertMetrics.map((metric) => {
          const Icon = metric.icon;
          const hasIssue = metric.isAlert && metric.value > 0;
          return (
            <div 
              key={metric.label} 
              className={`bg-white rounded-xl shadow-sm border p-4 ${hasIssue ? 'border-red-200 bg-red-50' : 'border-slate-200'}`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">{metric.label}</p>
                  <p className={`text-3xl font-bold mt-1 ${hasIssue ? 'text-red-600' : 'text-slate-900'}`}>{metric.value}</p>
                </div>
                <div className={`p-3 rounded-xl ${metric.bgColor}`}>
                  <Icon size={24} className={hasIssue ? 'text-red-600' : 'text-slate-600'} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Publishing Progress Bar */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
        <h2 className="text-lg font-bold text-slate-800 mb-3 flex items-center gap-2">
          <TrendingUp className="text-cyan-500" size={20} />
          Publishing Progress
        </h2>
        <div className="space-y-3">
          <div className="flex justify-between text-sm mb-1">
            <span className="text-slate-600">Overall Progress</span>
            <span className="font-semibold text-slate-800">
              {overview?.completed || 0} / {overview?.total_planned || 0} posts completed
            </span>
          </div>
          <div className="w-full bg-slate-200 rounded-full h-4 overflow-hidden">
            <div className="h-4 flex">
              <div 
                className="bg-green-500 h-full transition-all" 
                style={{ width: `${overview?.completion_rate || 0}%` }}
                title={`Completed: ${overview?.completed || 0}`}
              />
              <div 
                className="bg-blue-500 h-full transition-all" 
                style={{ width: `${((overview?.in_progress || 0) / (overview?.total_planned || 1)) * 100}%` }}
                title={`In Progress: ${overview?.in_progress || 0}`}
              />
            </div>
          </div>
          <div className="flex gap-4 text-xs">
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 bg-green-500 rounded-full"></span>
              Published ({overview?.completed || 0})
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 bg-blue-500 rounded-full"></span>
              In Progress ({overview?.in_progress || 0})
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 bg-slate-300 rounded-full"></span>
              Pending ({(overview?.total_planned || 0) - (overview?.completed || 0) - (overview?.in_progress || 0)})
            </span>
          </div>
        </div>
      </div>

      {/* Weekly Publishing Schedule */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <CalendarIcon className="text-cyan-500" size={20} />
            Weekly Publishing Schedule
          </h2>
          <Link to="/social-media/calendar" className="text-sm text-cyan-600 hover:text-cyan-700 flex items-center gap-1">
            View Calendar <ArrowRight size={14} />
          </Link>
        </div>
        <div className="grid grid-cols-7 gap-2">
          {(overview?.weekly_schedule || []).map((day, index) => (
            <div 
              key={day.date} 
              className={`rounded-lg p-3 text-center border ${
                day.is_today 
                  ? 'bg-cyan-50 border-cyan-200 ring-2 ring-cyan-500' 
                  : day.post_count > 0 
                    ? 'bg-blue-50 border-blue-200' 
                    : 'bg-slate-50 border-slate-200'
              }`}
            >
              <p className={`text-xs font-medium ${day.is_today ? 'text-cyan-600' : 'text-slate-500'}`}>
                {day.day_name}
              </p>
              <p className={`text-lg font-bold ${day.is_today ? 'text-cyan-700' : 'text-slate-800'}`}>
                {day.day_number}
              </p>
              <p className="text-xs text-slate-500">{day.month}</p>
              <div className={`mt-2 text-sm font-semibold rounded-full px-2 py-0.5 ${
                day.post_count > 0 
                  ? day.is_today ? 'bg-cyan-500 text-white' : 'bg-blue-500 text-white'
                  : 'bg-slate-200 text-slate-500'
              }`}>
                {day.post_count} {day.post_count === 1 ? 'post' : 'posts'}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status Distribution Pie Chart */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <h2 className="text-lg font-bold text-slate-800 mb-4">Status Distribution</h2>
          {statusData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={statusData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, value }) => `${name}: ${value}`}
                >
                  {statusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={STATUS_COLORS[entry.name] || '#8884d8'} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-center text-slate-500 py-12">No data available</p>
          )}
        </div>

        {/* Platform Distribution Bar Chart */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <h2 className="text-lg font-bold text-slate-800 mb-4">Platform Distribution</h2>
          {platformData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={platformData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" tick={{ fontSize: 12 }} stroke="#64748b" />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 12 }} width={80} stroke="#64748b" />
                <Tooltip />
                <Bar dataKey="value" fill="#06b6d4" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-center text-slate-500 py-12">No platform data available</p>
          )}
        </div>
      </div>

      {/* Recently Posted */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <CheckCircle className="text-green-500" size={20} />
            Recently Posted (Last 7 Days)
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {(overview?.recently_posted || []).length > 0 ? (
            overview.recently_posted.map((post) => (
              <div key={post.id} className="p-3 bg-green-50 rounded-lg border border-green-100">
                <p className="font-medium text-slate-800 text-sm truncate">{post.content_name}</p>
                <div className="flex items-center justify-between mt-1">
                  <div className="flex items-center gap-1">
                    {post.platforms?.slice(0, 3).map(p => (
                      <span key={p}>{getPlatformIcon(p, 14)}</span>
                    ))}
                  </div>
                  <span className="text-xs text-green-600">
                    {post.posted_date ? format(new Date(post.posted_date), 'MMM dd') : 'N/A'}
                  </span>
                </div>
              </div>
            ))
          ) : (
            <p className="col-span-full text-center text-slate-500 py-6 text-sm">No posts completed in the last 7 days</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default SocialMediaDashboard;
